// The encounter director (decision 0049; the thin spot "the encounter director", read against Left 4 Dead's AI
// director: build-up, peak, relax). An encounter is data: waves of spawn groups at named points. The director runs
// one wave at a time; a wave starts after the previous is cleared (or thinned to `until` alive), a quiet stretch has
// passed, and the player's intensity (raised by damage taken and kills near them, decaying over time) has fallen
// under the relax threshold, so a hard wave is followed by a breather. The game spawns and reports; the director
// keeps only plain data and draws no random numbers.

/** One spawn group: `count` of an enemy `kind` at a named spawn point. */
export interface SpawnGroup {
  kind: string;
  count: number;
  at: string;
}

/** A wave: its groups and when it may start. */
export interface WaveDef {
  id: string;
  groups: SpawnGroup[];
  /** Seconds of quiet after the previous wave is cleared before this one (default the encounter's `quiet`). */
  quiet?: number;
  /** The previous wave counts as cleared at this many alive or fewer (default 0). */
  until?: number;
}

/** An encounter as data. */
export interface EncounterDef {
  id: string;
  waves: WaveDef[];
  /** Start over from the first wave after the last (a test arena's endless loop). */
  loop?: boolean;
  /** Default quiet between waves (s). */
  quiet: number;
  /** Intensity (0 to 1): what a point of damage taken adds, what a kill near the player adds, the decay per second,
   *  and the level under which the next wave may start. */
  intensity: { perDamage: number; perKill: number; decay: number; relaxBelow: number };
}

/** Where the director is. */
export type DirectorPhase = "quiet" | "wave" | "done";

/** The director's state: plain data. */
export interface DirectorState {
  phase: DirectorPhase;
  /** The wave running or next. */
  wave: number;
  /** Laps through a looping encounter. */
  lap: number;
  /** Seconds left in the quiet stretch. */
  quietLeft: number;
  intensity: number;
  /** The ids the current wave spawned that are still alive. */
  alive: number[];
  /** Waves started since the encounter began. */
  started: number;
}

/** What the game does for the director. */
export interface DirectorHooks {
  /** Spawn one enemy of `kind` at spawn point `at` (the `index`th of its group); returns its id. */
  spawn(kind: string, at: string, index: number): number;
}

/** Runs one encounter. */
export class EncounterDirector {
  state: DirectorState;

  constructor(
    readonly def: EncounterDef,
    private hooks: DirectorHooks,
  ) {
    if (def.waves.length === 0) throw new Error(`encounter "${def.id}": no waves`);
    this.state = { phase: "quiet", wave: 0, lap: 0, quietLeft: def.waves[0]!.quiet ?? def.quiet, intensity: 0, alive: [], started: 0 };
  }

  /** The player took damage (raises intensity). */
  hurt(amount: number): void {
    this.state.intensity = Math.min(1, this.state.intensity + amount * this.def.intensity.perDamage);
  }

  /** An enemy died: drop it from the wave's living, and raise intensity when it died near the player. */
  defeated(id: number, nearPlayer: boolean): void {
    this.state.alive = this.state.alive.filter((a) => a !== id);
    if (nearPlayer) this.state.intensity = Math.min(1, this.state.intensity + this.def.intensity.perKill);
  }

  /** Once per sim step. */
  step(dt: number): void {
    const s = this.state;
    s.intensity = Math.max(0, s.intensity - this.def.intensity.decay * dt);
    if (s.phase === "done") return;
    if (s.phase === "wave") {
      const next = this.def.waves[s.wave + 1] ?? (this.def.loop ? this.def.waves[0] : undefined);
      const until = next?.until ?? 0;
      if (s.alive.length > until) return;
      // cleared (or thinned): on to the next wave's quiet stretch
      s.wave++;
      if (s.wave >= this.def.waves.length) {
        if (!this.def.loop) {
          s.phase = "done";
          return;
        }
        s.wave = 0;
        s.lap++;
      }
      s.phase = "quiet";
      s.quietLeft = this.def.waves[s.wave]!.quiet ?? this.def.quiet;
      return;
    }
    // quiet: wait out the stretch, then for the intensity to relax
    s.quietLeft = Math.max(0, s.quietLeft - dt);
    if (s.quietLeft > 1e-9 || s.intensity >= this.def.intensity.relaxBelow) return;
    const w = this.def.waves[s.wave]!;
    for (const g of w.groups) for (let i = 0; i < g.count; i++) s.alive.push(this.hooks.spawn(g.kind, g.at, i));
    s.phase = "wave";
    s.started++;
  }

  snapshot(): DirectorState {
    return structuredClone(this.state);
  }

  restore(s: DirectorState): void {
    this.state = structuredClone(s);
  }
}
